// Данные ученика в localStorage не должны переходить между аккаунтами на одном
// браузере (QA 2026-09-24: новый пользователь видел «Продолжить урок» чужого
// аккаунта). Ключи с личными данными перечислены здесь; при выходе они
// стираются, а при входе под другим user id — тоже (владелец хранится в
// OWNER_KEY). Удобства устройства (размер текста, контраст, медленная
// озвучка, автоозвучка чата, отметка о cookie) сюда НЕ входят и остаются.

export const OWNER_KEY = 'tk-storage-owner'

// Событие для подписчиков useSyncExternalStore в этой же вкладке:
// 'storage' приходит только из других вкладок.
export const USER_STORAGE_EVENT = 'tk-user-storage'

// Префиксы ключей с данными конкретного ученика.
export const USER_KEY_PREFIXES = [
  'tk-lesson:', // снимок незаконченного урока (lib/lessons/resume.ts)
  'tk-blitz-record',
  'tk-dt-speed-record',
  'tk-gov-speed-record',
  'tk-harmony-speed-record',
  'tk-travel-favorites', // избранные фразы разговорника
] as const

export function isUserDataKey(key: string): boolean {
  return USER_KEY_PREFIXES.some((p) => key.startsWith(p))
}

function defaultStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

function notify() {
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(USER_STORAGE_EVENT))
  } catch {}
}

// Стирает все личные ключи и отметку владельца. Возвращает число стёртых ключей.
export function clearUserStorage(storage: Storage | null = defaultStorage()): number {
  if (!storage) return 0
  let removed = 0
  try {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i)
      if (k && isUserDataKey(k)) keys.push(k)
    }
    for (const k of keys) {
      storage.removeItem(k)
      removed++
    }
    storage.removeItem(OWNER_KEY)
  } catch {}
  if (removed) notify()
  return removed
}

// Вызывается, когда известен вошедший пользователь. Другой владелец — личные
// ключи прежнего стираются. Владельца ещё нет — всё остаётся новому аккаунту:
// с 16.10.2026 гость проходит первый урок без входа (lib/guest-lesson.ts), и
// его снимок урока должен пережить регистрацию, чтобы продолжить с того же
// шага. Чужой снимок сюда не попадёт: выход стирает личные ключи вместе с
// владельцем (clearUserStorage).
export function claimUserStorage(userId: string, storage: Storage | null = defaultStorage()): void {
  if (!storage || !userId) return
  try {
    const owner = storage.getItem(OWNER_KEY)
    if (owner === userId) return
    if (owner) clearUserStorage(storage)
    storage.setItem(OWNER_KEY, userId)
  } catch {}
  notify()
}

export function storageOwner(storage: Storage | null = defaultStorage()): string | null {
  try {
    return storage?.getItem(OWNER_KEY) ?? null
  } catch {
    return null
  }
}
